import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add, FolderOpen, Refresh } from '@mui/icons-material';
import { platformApi, Project, User } from '../services/platformApi';
import {
  getStatusColor,
  languageOptions,
  packageTypes,
  storyTypes,
} from './projectConstants';
import { useLanguage } from '../LanguageContext';
import { useAuth } from '../AuthContext';
import { formatTranslatedLabel } from '../i18n';

interface ProjectFormState {
  title: string;
  description: string;
  ownerId: string;
  storyType: string;
  packageType: string;
  language: string;
}

interface ProjectDashboardProps {
  scope?: 'project' | 'admin';
}

const initialFormState: ProjectFormState = {
  title: '',
  description: '',
  ownerId: '',
  storyType: 'life_book',
  packageType: 'guided',
  language: 'en',
};

const ProjectDashboard: React.FC<ProjectDashboardProps> = ({ scope = 'project' }) => {
  const { t } = useLanguage();
  const { currentUser } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formState, setFormState] = useState<ProjectFormState>(initialFormState);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [projectData, userData] = await Promise.all([
        platformApi.listProjects(),
        currentUser?.is_admin ? platformApi.listUsers() : Promise.resolve(currentUser ? [currentUser] : []),
      ]);
      setProjects(projectData);
      setUsers(userData);
      setFormState((current) => ({
        ...current,
        ownerId: current.ownerId || currentUser?.id.toString() || userData[0]?.id.toString() || '',
      }));
    } catch (err) {
      setError(t('couldNotLoadProjectWorkspaceData'));
    } finally {
      setLoading(false);
    }
  }, [currentUser, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const ownerById = useMemo(() => {
    return new Map(users.map((user) => [user.id, user]));
  }, [users]);

  const stats = useMemo(() => {
    const activeProjects = projects.filter(
      (project) => !['completed', 'cancelled'].includes(project.status)
    ).length;
    const reviewProjects = projects.filter((project) => project.status.includes('review')).length;
    const approvalProjects = projects.filter((project) => project.status === 'approval_pending').length;

    const projectStats = [
      { label: t('activeProjects'), value: activeProjects },
      { label: t('inReview'), value: reviewProjects },
      { label: t('pendingApproval'), value: approvalProjects },
    ];
    if (currentUser?.is_admin) {
      projectStats.push({ label: t('users'), value: users.length });
    }
    return projectStats;
  }, [currentUser, projects, t, users]);

  const handleLanguageChange = (language: string) => {
    setFormState((current) => ({ ...current, language }));
  };

  const handleCreateProject = async () => {
    const selectedLanguage = languageOptions.find((option) => option.value === formState.language);
    const ownerId = currentUser?.is_admin ? formState.ownerId : currentUser?.id.toString();
    if (!formState.title.trim() || !ownerId || !selectedLanguage) {
      setError(t('projectTitleOwnerAndLanguageRequired'));
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await platformApi.createProject({
        title: formState.title.trim(),
        description: formState.description.trim() || undefined,
        owner_id: Number(ownerId),
        story_type: formState.storyType,
        package_type: formState.packageType,
        primary_language_code: selectedLanguage.value,
        locale: selectedLanguage.locale,
        text_direction: selectedLanguage.direction,
      });
      setDialogOpen(false);
      setFormState({
        ...initialFormState,
        ownerId: currentUser?.id.toString() || users[0]?.id.toString() || '',
      });
      await loadData();
    } catch (err) {
      setError(t('couldNotCreateProject'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} mb={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            {scope === 'admin' ? t('adminProjects') : t('projectWorkspace')}
          </Typography>
          <Typography color="text.secondary">
            {scope === 'admin' ? t('adminProjectsDescription') : t('projectWorkspaceDescription')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton aria-label={t('refreshProjects')} onClick={loadData}>
            <Refresh />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              setFormState((current) => ({
                ...current,
                ownerId: currentUser?.is_admin ? current.ownerId || users[0]?.id.toString() || '' : currentUser?.id.toString() || '',
              }));
              setDialogOpen(true);
            }}
            disabled={!currentUser}
          >
            {t('newProject')}
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {currentUser?.is_admin && users.length === 0 && !loading && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('createUserFirst')}
        </Alert>
      )}

      <Grid container spacing={2} mb={3}>
        {stats.map((stat) => (
          <Grid item xs={6} md={3} key={stat.label}>
            <Paper variant="outlined" sx={{ p: 2, minHeight: 92 }}>
              <Typography variant="body2" color="text.secondary">
                {stat.label}
              </Typography>
              <Typography variant="h4" component="div">
                {stat.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('project')}</TableCell>
              <TableCell>{t('status')}</TableCell>
              <TableCell>{t('storyType')}</TableCell>
              <TableCell>{t('language')}</TableCell>
              <TableCell>{t('owner')}</TableCell>
              <TableCell align="right">{t('actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {projects.map((project) => {
              const owner = project.owner_id ? ownerById.get(project.owner_id) : undefined;
              return (
                <TableRow key={project.id} hover>
                  <TableCell>
                    <Typography fontWeight={700}>{project.title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {project.description || t('noProductionNotes')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                      <Chip
                        size="small"
                        label={formatTranslatedLabel(t, project.status)}
                        color={getStatusColor(project.status)}
                      />
                    </TableCell>
                  <TableCell>{formatTranslatedLabel(t, project.story_type)}</TableCell>
                  <TableCell>
                    {project.primary_language_code}
                    {project.text_direction === 'rtl' ? ' / RTL' : ''}
                  </TableCell>
                  <TableCell>{owner?.display_name || owner?.email || t('unassigned')}</TableCell>
                  <TableCell align="right">
                    <Button
                      component={RouterLink}
                      to={`/projects/${project.id}`}
                      variant="text"
                      startIcon={<FolderOpen />}
                    >
                      {t('open')}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
            {projects.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography color="text.secondary" align="center" sx={{ py: 5 }}>
                    {t('noProjectsYet')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('createProject')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label={t('projectName')}
              value={formState.title}
              onChange={(event) => setFormState({ ...formState, title: event.target.value })}
              fullWidth
              required
            />
            <TextField
              label={t('goalsAndInstructions')}
              value={formState.description}
              onChange={(event) => setFormState({ ...formState, description: event.target.value })}
              fullWidth
              multiline
              minRows={3}
            />
            {currentUser?.is_admin && (
              <FormControl fullWidth required>
                <InputLabel id="owner-label">{t('projectManager')}</InputLabel>
                <Select
                  labelId="owner-label"
                  label={t('projectManager')}
                  value={formState.ownerId}
                  onChange={(event) => setFormState({ ...formState, ownerId: event.target.value })}
                >
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.id.toString()}>
                      {user.display_name || user.email}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <FormControl fullWidth>
              <InputLabel id="story-type-label">{t('storyType')}</InputLabel>
              <Select
                labelId="story-type-label"
                label={t('storyType')}
                value={formState.storyType}
                onChange={(event) => setFormState({ ...formState, storyType: event.target.value })}
              >
                {storyTypes.map((storyType) => (
                  <MenuItem key={storyType.value} value={storyType.value}>
                    {formatTranslatedLabel(t, storyType.value)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="package-type-label">{t('package')}</InputLabel>
              <Select
                labelId="package-type-label"
                label={t('package')}
                value={formState.packageType}
                onChange={(event) => setFormState({ ...formState, packageType: event.target.value })}
              >
                {packageTypes.map((packageType) => (
                  <MenuItem key={packageType.value} value={packageType.value}>
                    {formatTranslatedLabel(t, packageType.value)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="language-label">{t('primaryLanguage')}</InputLabel>
              <Select
                labelId="language-label"
                label={t('primaryLanguage')}
                value={formState.language}
                onChange={(event) => handleLanguageChange(event.target.value)}
              >
                {languageOptions.map((language) => (
                  <MenuItem key={language.value} value={language.value}>
                    {language.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t('cancel')}</Button>
          <Button variant="contained" onClick={handleCreateProject} disabled={saving}>
            {t('create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectDashboard;
